import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';

// Services
import { AnalyticsService } from 'src/app/services/analytics.service';

// Models
import { ReservationServiceCategory } from 'src/app/models/reservation/reservation-service-category.model';
import { ReservationRequestItem } from 'src/app/models/reservation/reservation-request.model';

import * as lodash from 'lodash';


@Component({
  selector: 'app-reservation-service',
  templateUrl: './reservation-services.component.html',
  styleUrls: ['./reservation-services.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReservationServicesComponent implements OnInit, OnDestroy {

  // #region -> Property - Component I/O <-
  // ----------

  @Input() reservationRequest$: Observable<ReservationRequestItem>;
  reservationRequest: ReservationRequestItem = new ReservationRequestItem({});
  reservationRequestSubscription: Subscription;
  @Output() reservationRequestChange = new EventEmitter<ReservationRequestItem>();

  @Input() serviceCategories$: Observable<{ [categoryId: string]: ReservationServiceCategory }>;
  serviceCategories: { [categoryId: string]: ReservationServiceCategory } = {};
  serviceCategoriesSubscription: Subscription;

  @Output() navigateToNext = new EventEmitter();

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property - Form <-
  // ----------

  form: FormGroup;
  formArrayKey = 'coreServices';
  get formArray(): FormArray {
    return this.form.get(this.formArrayKey) as FormArray;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property <-
  // ----------

  isErrorOnSave = false;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private fb: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    // Init the form
    this._initForm();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Lifecycle <-
  // ----------

  ngOnInit(): void {
    //
    this.serviceCategoriesSubscription = this.serviceCategories$.subscribe((newServiceCategories) => {
      // Empty the list if the incoming data is empty
      if (lodash.isEmpty(newServiceCategories)) {
        this.serviceCategories = {};
      } else {
        this.serviceCategories = newServiceCategories;
      }
      this.resetFormData(this.reservationRequest);

      // Trigger change detection when mutated.
      this.changeDetectorRef.markForCheck();
    });
    this.reservationRequestSubscription = this.reservationRequest$.subscribe((newReservationRequest) => {
      this.reservationRequest = this.reservationRequest.update({...newReservationRequest.json()});

      // For an empty "core services", reset the form data
      if (!this.reservationRequest.hasCoreServices()) {
        this.resetFormData(this.reservationRequest);
      }

      // Trigger change detection when mutated.
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    //
    this.reservationRequestSubscription.unsubscribe();
    this.serviceCategoriesSubscription.unsubscribe();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - UI <-
  // ----------


  handleSelectedServices(event: ReservationRequestItem): void {
    //
    this.reservationRequest = this.reservationRequest.update({...event.json()});
  }

  hasSelectionOnCoreServices(): boolean {
    if (this.reservationRequest) {
      return this.reservationRequest.hasCoreServices();
    }

    //
    return false;
  }

  canSave(): boolean {
    // User can save and continue once there is at least one selection.
    return this.hasSelectionOnCoreServices();
  }

  onSave(): void {
    this.reservationRequestChange.emit(this.reservationRequest);

    // Next
    // this.navigateToNext.emit();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method <-
  // ----------

  getServiceCategory(index: number): ReservationServiceCategory {
    //
    return this.formArray.at(index).value
      .linkedCategory as ReservationServiceCategory;
  }

  resetFormData(reservationRequest: ReservationRequestItem): void {
    // Init the form, again
    this._initForm();

    //
    for (const categoryId in this.serviceCategories) {
      //
      if (this.serviceCategories.hasOwnProperty(categoryId)) {
        //
        const serviceCategory = this.serviceCategories[
          categoryId
        ] as ReservationServiceCategory;
        if (serviceCategory.hasCoreServices()) {
          // Build the service array first
          const serviceFormArray = [];
          serviceCategory.coreServices.forEach((service) => {
            serviceFormArray.push(
              this.fb.group({
                id: [service.linkedService.id],
                name: [service.serviceName],
                description: [service.serviceDescription],
                price: [service.servicePrice],
                linkedService: [lodash.cloneDeep(service)],
                isSelected: [reservationRequest.includeCoreService(service.linkedService.id)],
              })
            );
          });

          // The form item for "category"
          this.formArray.push(
            this.fb.group({
              id: [serviceCategory.categoryId],
              name: [serviceCategory.categoryName],
              linkedCategory: [lodash.cloneDeep(serviceCategory)],
              isSelected: [reservationRequest.includeCoreCategory(serviceCategory.categoryId)],
              serviceFormArray: this.fb.array(serviceFormArray),
              description: [''],
              totalPrice: [reservationRequest.calcTotalPriceOfCoreServices(serviceCategory.categoryId)],
            })
          );
        }
      }
    }

    // Update the linked data entry.
    this.reservationRequest = this.reservationRequest.update({...reservationRequest.json()}).clone();
    this.changeDetectorRef.markForCheck();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  _initForm() {
    this.form = this.fb.group({
      [this.formArrayKey]: this.fb.array([]),
    });
  }

  // ----------
  // #endregion

  /////////////////////////////

}
